import { camelizeKeysPreservingAcronyms } from '@/common/api/keyCasing';
import { RESOLUTION_BUCKET_MS } from '@/features/telemetry/config';
import type { TelemetryResolution } from '@/features/telemetry/services/schemas';

/**
 * Raw (snake_case, wire-shaped) fixtures shared by two consumers:
 *
 * - `TelemetryService`'s `mock:` factories, which bypass axios entirely (see
 *   `src/common/api/apiMock.ts`) and so must hand back data already shaped the way
 *   `ApiClient` shapes a real response — the raw fixture is run through
 *   `camelizeKeysPreservingAcronyms`, the exact function the response interceptor uses.
 * - Storybook's MSW handlers, which serve the raw fixture over the network and let
 *   the real interceptor camelize it — exercising the same contract the live API does.
 *
 * There is exactly one fixture generator. Nothing here is pre-camelized by hand.
 */

export interface MockDeviceDefinition {
  id: number;
  name: string;
  deviceType: string;
}

/** Mirrors the five devices from the source Figma prototype. */
export const MOCK_DEVICES: MockDeviceDefinition[] = [
  { id: 1, name: 'Main Meter', deviceType: 'Meter' },
  { id: 2, name: 'Solar Inverter', deviceType: 'Inverter' },
  { id: 3, name: 'Storage Battery', deviceType: 'Battery' },
  { id: 4, name: 'Temp Sensor 1', deviceType: 'Sensor' },
  { id: 5, name: 'Backup Generator', deviceType: 'Inverter' },
];

/** Raw (snake_case) telemetry field catalog — the wire shape of TelemetryField. */
export const MOCK_TELEMETRY_FIELDS_RAW = [
  {
    variable: 'P_active_kw',
    label: 'Active power',
    unit: 'kW',
    value_type: 'float',
    aggregatable: true,
    // The real API sends `null`, not `{}`, for fields with no enum/state values.
    enum_choices: null,
    device_type: 'Meter',
  },
  {
    variable: 'communication_status',
    label: 'Communication',
    unit: null,
    value_type: 'string',
    aggregatable: false,
    enum_choices: { CONNECTED: 1, UNSTABLE: 2, DISCONNECTED: 0 },
    device_type: 'Meter',
  },
  {
    variable: 'P_active_kw',
    label: 'Active power',
    unit: 'kW',
    value_type: 'float',
    aggregatable: true,
    enum_choices: null,
    device_type: 'Inverter',
  },
  {
    variable: 'grid_status',
    label: 'Grid connection',
    unit: null,
    value_type: 'string',
    aggregatable: false,
    enum_choices: { ON_GRID: 1, OFF_GRID: 0, FAULT: 2 },
    device_type: 'Inverter',
  },
  {
    variable: 'P_active_kw',
    label: 'Active power',
    unit: 'kW',
    value_type: 'float',
    aggregatable: true,
    enum_choices: null,
    device_type: 'Battery',
  },
  {
    variable: 'soc_pct',
    label: 'State of charge',
    // Real API has also been observed sending the spelled-out form ('percent')
    // instead of '%' — see utils/unitDisplay.ts, which normalizes both.
    unit: '%',
    value_type: 'float',
    aggregatable: true,
    enum_choices: null,
    device_type: 'Battery',
  },
  {
    variable: 'bms_mode',
    label: 'BMS mode',
    unit: null,
    value_type: 'string',
    aggregatable: false,
    enum_choices: { NORMAL: 0, CHARGING: 1, BALANCING: 2, PROTECTION: 3 },
    device_type: 'Battery',
  },
  {
    variable: 'value_c',
    label: 'Temperature',
    unit: '°C',
    value_type: 'float',
    aggregatable: true,
    enum_choices: null,
    device_type: 'Sensor',
  },
  {
    variable: 'telemetry_status',
    label: 'Telemetry',
    unit: null,
    value_type: 'string',
    aggregatable: false,
    enum_choices: { OK: 0, DELAYED: 1, TIMEOUT: 2, ERROR: 3 },
    device_type: 'Sensor',
  },
];

interface NumericFieldDef {
  variable: string;
  generate: (seed: number, bucketTs: number) => number;
}

interface EnumFieldDef {
  variable: string;
  values: readonly string[];
}

const NUMERIC_FIELDS_BY_DEVICE_TYPE: Record<string, NumericFieldDef[]> = {
  Meter: [{ variable: 'P_active_kw', generate: (seed, ts) => activePower(seed, ts) }],
  Inverter: [
    { variable: 'P_active_kw', generate: (seed, ts) => Math.max(0, solarPower(seed, ts)) },
  ],
  Battery: [
    { variable: 'P_active_kw', generate: (seed, ts) => batteryPower(seed, ts) },
    { variable: 'soc_pct', generate: (seed, ts) => stateOfCharge(seed, ts) },
  ],
  Sensor: [{ variable: 'value_c', generate: (seed, ts) => temperature(seed, ts) }],
};

const ENUM_FIELDS_BY_DEVICE_TYPE: Record<string, EnumFieldDef[]> = {
  Meter: [{ variable: 'communication_status', values: ['connected', 'unstable', 'disconnected'] }],
  Inverter: [{ variable: 'grid_status', values: ['on-grid', 'off-grid', 'fault'] }],
  Battery: [{ variable: 'bms_mode', values: ['normal', 'charging', 'balancing', 'protection'] }],
  Sensor: [{ variable: 'telemetry_status', values: ['ok', 'delayed', 'timeout', 'error'] }],
};

/** Deterministic pseudo-noise in [-0.15, 0.15], seeded per device + bucket. */
function noise(seed: number, ts: number): number {
  return (Math.sin(seed * 31.7 + ts * 0.00017) * 0.5 + 0.5) * 0.3 - 0.15;
}

function hourOfDay(ts: number): number {
  const date = new Date(ts);
  return date.getUTCHours() + date.getUTCMinutes() / 60;
}

function activePower(seed: number, ts: number): number {
  const hour = hourOfDay(ts);
  return round2(2.5 + 1.5 * Math.sin(((hour - 14) / 12) * Math.PI) + noise(seed, ts) * 2);
}

function solarPower(seed: number, ts: number): number {
  const hour = hourOfDay(ts);
  const base = 3.5 * Math.sin(((hour - 6) / 12) * Math.PI);
  return round2(base > 0 ? base + noise(seed, ts) * 0.5 : 0);
}

function batteryPower(seed: number, ts: number): number {
  const hour = hourOfDay(ts);
  const solar = Math.max(0, 3.5 * Math.sin(((hour - 6) / 12) * Math.PI));
  const load = 2.5 + 1.5 * Math.sin(((hour - 14) / 12) * Math.PI);
  return round2((solar - load) * 0.4 + noise(seed, ts));
}

function stateOfCharge(seed: number, ts: number): number {
  const hour = hourOfDay(ts);
  const value = 50 + 30 * Math.sin(((hour - 10) / 14) * Math.PI) + noise(seed, ts) * 10;
  return round1(Math.min(100, Math.max(0, value)));
}

function temperature(seed: number, ts: number): number {
  const hour = hourOfDay(ts);
  return round1(20 + 8 * Math.sin(((hour - 14) / 12) * Math.PI) + noise(seed, ts) * 3);
}

function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

/** djb2-ish string hash, used only to pick a deterministic enum starting index. */
function hashString(value: string): number {
  let hash = 5381;
  for (let i = 0; i < value.length; i++) {
    hash = (hash * 33) ^ value.charCodeAt(i);
  }
  return Math.abs(hash);
}

function enumValueAt(values: readonly string[], seed: number, bucketIndex: number): string {
  // Changes roughly every ~6 buckets so a timeline has multiple segments, not one per bucket.
  const step = Math.floor(bucketIndex / 6);
  return values[(hashString(String(seed)) + step) % values.length];
}

export interface BuildMockTelemetryParams {
  from: Date;
  to: Date;
  resolution: TelemetryResolution;
  /** Optional device-id filter — omit for every mock device. */
  deviceIds?: number[];
}

/** Raw telemetry response, shaped exactly like the OpenAPI example (snake_case). */
export function buildMockTelemetryResponseRaw(params: BuildMockTelemetryParams) {
  const bucketMs = RESOLUTION_BUCKET_MS[params.resolution];
  const devices = params.deviceIds
    ? MOCK_DEVICES.filter((device) => params.deviceIds!.includes(device.id))
    : MOCK_DEVICES;

  const fromMs = params.from.getTime();
  const toMs = params.to.getTime();

  return {
    result: devices.map((device) => {
      const numericFields = NUMERIC_FIELDS_BY_DEVICE_TYPE[device.deviceType] ?? [];
      const enumFields = ENUM_FIELDS_BY_DEVICE_TYPE[device.deviceType] ?? [];

      const series: Record<string, unknown>[] = [];
      let bucketIndex = 0;
      for (let ts = fromMs; ts < toMs; ts += bucketMs, bucketIndex++) {
        const point: Record<string, unknown> = { time: new Date(ts).toISOString() };

        for (const field of numericFields) {
          const value = field.generate(device.id, ts);
          if (params.resolution === '1min') {
            point[field.variable] = value;
          } else {
            point[`${field.variable}_avg`] = value;
            point[`${field.variable}_min`] = round2(value - Math.abs(noise(device.id, ts)));
            point[`${field.variable}_max`] = round2(value + Math.abs(noise(device.id, ts + 1)));
          }
        }

        for (const field of enumFields) {
          const value = enumValueAt(field.values, device.id, bucketIndex);
          point[params.resolution === '1min' ? field.variable : `${field.variable}_last`] = value;
        }

        if (params.resolution === '15min') {
          point.sample_count = 15;
        }

        series.push(point);
      }

      return {
        device_id: device.id,
        device_name: device.name,
        device_type: device.deviceType,
        series,
      };
    }),
  };
}

/** `MOCK_TELEMETRY_FIELDS_RAW`, camelized exactly the way a real response would be. */
export function buildMockTelemetryFields() {
  return camelizeKeysPreservingAcronyms(MOCK_TELEMETRY_FIELDS_RAW);
}

/** `buildMockTelemetryResponseRaw`, camelized exactly the way a real response would be. */
export function buildMockTelemetryResponse(params: BuildMockTelemetryParams) {
  return camelizeKeysPreservingAcronyms(buildMockTelemetryResponseRaw(params));
}
